import {
  type ReactNode,
  type Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useReducedMotion } from 'motion/react';
import { cn } from '@/lib/utils';
import type {
  KorzaTorusHandle,
  KorzaTorusOptions,
} from '@/components/brand/korza-torus/korza-torus-scene';

export interface KorzaTorusControls {
  /** Short surge of speed and ripple, e.g. right before navigating away. */
  boost(): void;
  /**
   * Glides the ring to a new offset and zoom (units of `offsetX`/`offsetY`/
   * `zoom`); at once under reduced motion. Called before the scene has
   * loaded, the scene starts there.
   */
  moveTo(x: number, y: number, zoom: number): void;
}

export interface KorzaTorusProps
  extends Pick<KorzaTorusOptions, 'zoom' | 'offsetX' | 'offsetY'> {
  className?: string;
  /** Rendered instead of the canvas when WebGL (or the three chunk) fails. */
  fallback?: ReactNode;
  /** Canvas opacity once loaded (default: fully visible). */
  opacity?: number;
  ref?: Ref<KorzaTorusControls>;
}

/**
 * The brand book's chrome torus, live in WebGL. `three` is loaded on demand
 * so it only weighs on the screens that show it; the ring tilts toward the
 * pointer anywhere on the page and holds still under reduced motion.
 */
export function KorzaTorus({
  zoom,
  offsetX,
  offsetY,
  className,
  fallback = null,
  opacity,
  ref,
}: KorzaTorusProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const handleRef = useRef<KorzaTorusHandle | null>(null);
  /** Last `moveTo`, so a (re)mounted scene starts where it was sent. */
  const placementRef = useRef<{ x: number; y: number; zoom: number } | null>(
    null
  );
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>(
    'loading'
  );
  const reducedMotion = useReducedMotion() ?? false;

  useImperativeHandle(
    ref,
    () => ({
      boost: () => handleRef.current?.boost(),
      moveTo: (x, y, zoom) => {
        placementRef.current = { x, y, zoom };
        handleRef.current?.moveTo(x, y, zoom);
      },
    }),
    []
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cancelled = false;

    const onPointerMove = (event: PointerEvent) => {
      handleRef.current?.setPointer(
        (event.clientX / window.innerWidth) * 2 - 1,
        (event.clientY / window.innerHeight) * 2 - 1
      );
    };

    import('@/components/brand/korza-torus/korza-torus-scene')
      .then(({ mountKorzaTorus }) => {
        if (cancelled) return;
        const placement = placementRef.current;
        const handle = mountKorzaTorus(canvas, {
          zoom: placement?.zoom ?? zoom,
          offsetX: placement?.x ?? offsetX,
          offsetY: placement?.y ?? offsetY,
          reducedMotion,
        });
        handleRef.current = handle;
        setStatus(handle ? 'ready' : 'failed');
      })
      .catch(() => {
        if (!cancelled) setStatus('failed');
      });
    window.addEventListener('pointermove', onPointerMove);

    return () => {
      cancelled = true;
      window.removeEventListener('pointermove', onPointerMove);
      handleRef.current?.destroy();
      handleRef.current = null;
    };
  }, [zoom, offsetX, offsetY, reducedMotion]);

  if (status === 'failed') return fallback;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-testid="korza-torus"
      className={cn(
        'block transition-opacity duration-700 ease-out',
        status === 'ready' ? 'opacity-100' : 'opacity-0',
        className
      )}
      style={
        status === 'ready' && opacity !== undefined ? { opacity } : undefined
      }
    />
  );
}
